import { PropertyHelper } from '@/helpers/property.helper';
import { UserHelper } from '@/helpers/user.helper';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import {
  ActionState,
  ConnectorCode,
  ConnectorSync,
  ConnectorSyncRequest,
  ConnectorsService,
  PropertiesService,
  RealProperty
} from '@eemo/api-client';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { IConnectorProperty } from '../connector-user/connector-user.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { RouterLink } from '@angular/router';
import { NgTemplateOutlet, NgIf, NgFor } from '@angular/common';
import { ModalBaseComponent } from '../base/modal-base/modal-base.component';

export class ConnectorSyncDialogData {
  userId: string;
  connectorCode: ConnectorCode;
  connectorProperty: IConnectorProperty;
}

@Component({
    selector: 'app-connector-sync-dialog',
    templateUrl: './connector-sync-dialog.component.html',
    styleUrls: ['./connector-sync-dialog.component.scss'],
    standalone: true,
    imports: [ModalBaseComponent, NgTemplateOutlet, MatDialogModule, NgIf, RouterLink, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, MatOptionModule, NgFor, MatCheckboxModule, MatProgressBarModule, TranslateModule]
})
export class ConnectorSyncDialogComponent implements OnInit, OnDestroy {
  dialogTitle = 'connectorSyncDialog.title';
  isLoading = false;
  syncSub = new Subscription();
  getConnectorSyncSub = new Subscription();
  availableExistingProperty: RealProperty[] = [];
  availableExistingPropertyNames: { [key: string]: string } = {};

  submitLabel = 'START';
  isFirstSync = true;
  syncInProgress = false;
  syncDone = false;
  syncedWithExistingProperty = false;

  currentSync: ConnectorSync;
  timer: NodeJS.Timeout;
  ActionState = ActionState;

  get form_existingProperty() {
    return this.form.get('existingProperty') as FormControl<string>;
  }

  get form_syncCoreInfos() {
    return this.form.get('syncCoreInfos') as FormControl<boolean>;
  }

  get form_syncAttributes() {
    return this.form.get('syncAttributes') as FormControl<boolean>;
  }

  get form_syncPhotos() {
    return this.form.get('syncPhotos') as FormControl<boolean>;
  }

  form: UntypedFormGroup;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ConnectorSyncDialogData,
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<ConnectorSyncDialogComponent>,
    private connectorService: ConnectorsService,
    private propService: PropertiesService,
    private translate: TranslateService,
    private userHelper: UserHelper,
    private propHelper: PropertyHelper
  ) {
    this.isFirstSync = !this.data.connectorProperty.synced;
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      existingProperty: ['NEW'],
      syncCoreInfos: [this.isFirstSync],
      syncAttributes: [true],
      syncPhotos: [true]
    });
    if (this.isFirstSync) {
      // build the available existing property to sync with
      this.propService.getRealPropertyList().subscribe((props) => {
        this.connectorService
          .getConnectorUserPropertyList(this.data.userId, this.data.connectorCode)
          .subscribe((connectors) => {
            // the property must be owned and not already been synced
            const syncedProps = connectors.filter((c) => c.eemoUid).map((c) => c.eemoUid);
            props.list
              .filter(
                (prop) =>
                  prop.type === this.data.connectorProperty.type &&
                  this.userHelper.isPropertyOwner(prop) &&
                  !syncedProps[prop.uid]
              )
              .forEach((prop) => {
                this.availableExistingProperty.push(prop);
                this.availableExistingPropertyNames[prop.uid] = prop.name;
              });
          });
      });
    } else {
      // core infos can't be sync if not first sync
      this.form_syncCoreInfos.disable();
    }
  }

  ngOnDestroy(): void {
    this.syncSub.unsubscribe();
    this.getConnectorSyncSub.unsubscribe();
    this.stopAutoRefresh();
  }

  onSubmit(): void {
    if (this.syncDone && this.isRedirectToPropertyConfiguration()) {
      // first sync is done
      this.dialogRef.close(this.currentSync.property.uid);
    } else {
      this.form.markAllAsTouched();
      if (this.form.valid) {
        this.sync();
      }
    }
  }

  afterGetConnectorSync(currentSync: ConnectorSync) {
    this.currentSync = currentSync;

    () => {
      if (this.isFirstSync) this.submitLabel = 'NEXT';
    };
    if (!currentSync || currentSync.state !== ActionState.IN_PROGRESS) {
      this.stopAutoRefresh();
      this.syncDone = true;
      this.syncInProgress = false;
      if (this.isRedirectToPropertyConfiguration()) {
        this.submitLabel = 'NEXT';
      }
    }
  }

  isRedirectToPropertyConfiguration() {
    return (
      this.currentSync &&
      this.currentSync.state === ActionState.SUCCESS &&
      this.isFirstSync &&
      this.form_existingProperty.value === 'NEW'
    );
  }

  startAutoRefresh() {
    this.timer = setInterval(() => this.getConnectorSync(), 2000);
  }

  stopAutoRefresh() {
    clearInterval(this.timer);
  }

  // ---------------------------------------- //
  //                   APIS                   //
  // ---------------------------------------- //
  getConnectorSync() {
    this.isLoading = true;
    this.getConnectorSyncSub = this.connectorService
      .getConnectorUserSync(this.data.userId, this.data.connectorCode, this.currentSync.uid)
      .subscribe({
        next: (sync: ConnectorSync) => {
          this.isLoading = false;
          this.afterGetConnectorSync(sync);
        },
        error: (err: any) => {
          this.isLoading = false;
        }
      });
  }

  sync() {
    this.form.disable();
    this.isLoading = true;
    this.syncInProgress = true;
    const body = this.getSyncBody();
    this.syncedWithExistingProperty = body.existingPropertyId !== null;
    this.syncSub = this.connectorService
      .syncConnectorUserProperty(this.data.userId, this.data.connectorCode, body)
      .subscribe({
        next: (uid: string) => {
          this.isLoading = false;
          this.currentSync = { uid, state: ActionState.IN_PROGRESS };
          this.startAutoRefresh();
        },
        error: () => {
          this.isLoading = false;
          this.syncInProgress = false;
        }
      });
  }

  getSyncBody(): ConnectorSyncRequest {
    return {
      externalId: this.data.connectorProperty.externalId,
      existingPropertyId: this.form_existingProperty.value !== 'NEW' ? this.form_existingProperty.value : null,
      syncCoreInfos: this.form_syncCoreInfos.value,
      syncAttributes: this.form_syncAttributes.value,
      syncPhotos: this.form_syncPhotos.value
    };
  }
}
